import * as Y from 'yjs';
import type { Socket } from 'socket.io-client';
import type { Node as ReactFlowNode, Edge as ReactFlowEdge } from 'reactflow';
import type { NodeData } from '@/types/node.types';
import type { LinkData } from '@/types/link.types';
import { getSocket } from '@/collaboration/socket';

// ---- Awareness types ----

export interface CursorPosition {
  x: number;
  y: number;
}

export interface AwarenessUser {
  id: string;
  firstName: string;
  lastName: string;
  color: string;
}

export interface AwarenessData {
  cursor: CursorPosition;
  user: AwarenessUser;
}

export interface RemoteCursor {
  clientId: string;
  cursor: CursorPosition;
  user: AwarenessUser;
}

// ---- Helpers ----

/**
 * React Flow enriches nodes/edges at runtime with internal fields that can hold
 * DOM references (e.g. handleBounds) which crash JSON.stringify with circular
 * structure errors. We serialize only the canonical fields that Yjs needs.
 */
// Note: `selected` and `dragging` are per-client UI state and are intentionally
// excluded from the whitelist to avoid propagating them across users.
const NODE_KEYS = [
  'id', 'type', 'position', 'positionAbsolute', 'data', 'style', 'className',
  'width', 'height', 'zIndex', 'hidden', 'dragHandle', 'draggable', 'selectable',
  'parentNode', 'extent', 'expandParent', 'ariaLabel',
] as const;

const EDGE_KEYS = [
  'id', 'source', 'target', 'sourceHandle', 'targetHandle', 'type', 'data',
  'style', 'className', 'label', 'labelStyle', 'labelShowBg', 'labelBgStyle',
  'labelBgPadding', 'labelBgBorderRadius', 'markerStart', 'markerEnd',
  'animated', 'hidden', 'selected', 'zIndex', 'ariaLabel', 'interactionWidth',
] as const;

/**
 * JSON stringify that silently drops DOM nodes, functions, and circular refs.
 * React Flow and user components can leak these into node/edge shapes at runtime.
 */
function safeStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  return JSON.stringify(value, (_key, v) => {
    if (typeof v === 'function') return undefined;
    if (typeof v === 'object' && v !== null) {
      if (typeof Node !== 'undefined' && v instanceof Node) return undefined;
      if (seen.has(v as object)) return undefined;
      seen.add(v as object);
    }
    return v;
  });
}

function pickSerializable<T extends Record<string, unknown>>(
  src: T,
  keys: readonly string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const v = (src as Record<string, unknown>)[k];
    if (v !== undefined) out[k] = v;
  }
  return JSON.parse(safeStringify(out));
}

/** Decode a base64 string to Uint8Array. */
function b64ToUint8(b64: string): Uint8Array {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    arr[i] = bin.charCodeAt(i);
  }
  return arr;
}

/** Encode a Uint8Array to base64 string. */
function uint8ToB64(arr: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < arr.length; i++) {
    bin += String.fromCharCode(arr[i]);
  }
  return btoa(bin);
}

// ---- Provider ----

export class YjsProvider {
  readonly doc: Y.Doc;
  private readonly nodesMap: Y.Map<unknown>;
  private readonly edgesMap: Y.Map<unknown>;
  private readonly socket: Socket;
  private readonly canvasId: string;
  private isRemoteUpdate = false;
  private isLocalUpdate = false;
  private disposed = false;

  // Callbacks
  private nodesChangeCallbacks: Array<(nodes: ReactFlowNode<NodeData>[]) => void> = [];
  private edgesChangeCallbacks: Array<(edges: ReactFlowEdge<LinkData>[]) => void> = [];
  private remoteCursorsCallbacks: Array<(cursors: RemoteCursor[]) => void> = [];

  // Remote cursors state
  private remoteCursors = new Map<string, RemoteCursor>();

  // Bound handlers for cleanup
  private handleSyncState: (data: { state: string }) => void;
  private handleRemoteUpdate: (data: { canvasId: string; update: string }) => void;
  private handleAwarenessUpdate: (data: { clientId: string; awareness: AwarenessData }) => void;
  private handleDocUpdate: (update: Uint8Array, origin: unknown) => void;

  constructor(canvasId: string) {
    this.canvasId = canvasId;
    this.doc = new Y.Doc();
    this.nodesMap = this.doc.getMap('nodes');
    this.edgesMap = this.doc.getMap('edges');
    this.socket = getSocket();

    // ---- Bind handlers ----

    this.handleSyncState = (data: { state: string }) => {
      if (this.disposed) return;
      if (!data || typeof data.state !== 'string') return;
      this.isRemoteUpdate = true;
      try {
        const state = b64ToUint8(data.state);
        Y.applyUpdate(this.doc, state);
      } finally {
        this.isRemoteUpdate = false;
      }
    };

    this.handleRemoteUpdate = (data: { canvasId: string; update: string }) => {
      if (this.disposed) return;
      if (data.canvasId !== this.canvasId) return;
      this.isRemoteUpdate = true;
      try {
        const update = b64ToUint8(data.update);
        Y.applyUpdate(this.doc, update);
      } finally {
        this.isRemoteUpdate = false;
      }
    };

    this.handleAwarenessUpdate = (data: { clientId: string; awareness: AwarenessData }) => {
      if (this.disposed) return;
      this.remoteCursors.set(data.clientId, {
        clientId: data.clientId,
        cursor: data.awareness.cursor,
        user: data.awareness.user,
      });
      this.emitRemoteCursors();
    };

    this.handleDocUpdate = (update: Uint8Array, _origin: unknown) => {
      if (this.disposed) return;
      // Only broadcast updates originating from local mutations (not remote applies)
      if (this.isRemoteUpdate) return;
      // If the update was triggered by our own setNodes/setEdges, we flag it
      // but we still need to broadcast to other clients.
      this.socket.emit('yjs-update', {
        canvasId: this.canvasId,
        update: uint8ToB64(update),
      });
    };

    // ---- Register listeners ----

    this.socket.on('sync-state', this.handleSyncState);
    this.socket.on('yjs-update', this.handleRemoteUpdate);
    this.socket.on('awareness-update', this.handleAwarenessUpdate);

    // Listen for Y.Doc updates
    this.doc.on('update', this.handleDocUpdate);

    // Observe map changes to notify subscribers
    this.nodesMap.observe(() => {
      if (this.isLocalUpdate) return;
      this.emitNodesChange();
    });

    this.edgesMap.observe(() => {
      if (this.isLocalUpdate) return;
      this.emitEdgesChange();
    });

    // ---- Join the room ----

    this.socket.emit('join-canvas', { canvasId: this.canvasId });
  }

  // ---- Public map accessors ----

  getNodesMap(): Y.Map<unknown> {
    return this.nodesMap;
  }

  getEdgesMap(): Y.Map<unknown> {
    return this.edgesMap;
  }

  // ---- Write nodes/edges into Yjs ----

  setNodes(nodes: ReactFlowNode<NodeData>[]): void {
    this.isLocalUpdate = true;
    this.doc.transact(() => {
      // Build a set of incoming node ids for cleanup
      const incomingIds = new Set(nodes.map((n) => n.id));

      // Remove nodes that no longer exist
      for (const key of Array.from(this.nodesMap.keys())) {
        if (!incomingIds.has(key as string)) {
          this.nodesMap.delete(key as string);
        }
      }

      // Upsert every node
      for (const node of nodes) {
        const existing = this.nodesMap.get(node.id) as Record<string, unknown> | undefined;
        const serialized = pickSerializable(node as unknown as Record<string, unknown>, NODE_KEYS);
        // Only write if actually changed (avoid unnecessary Yjs events)
        if (!existing || JSON.stringify(existing) !== JSON.stringify(serialized)) {
          this.nodesMap.set(node.id, serialized);
        }
      }
    });
    this.isLocalUpdate = false;
  }

  setEdges(edges: ReactFlowEdge<LinkData>[]): void {
    this.isLocalUpdate = true;
    this.doc.transact(() => {
      const incomingIds = new Set(edges.map((e) => e.id));

      for (const key of Array.from(this.edgesMap.keys())) {
        if (!incomingIds.has(key as string)) {
          this.edgesMap.delete(key as string);
        }
      }

      for (const edge of edges) {
        const existing = this.edgesMap.get(edge.id) as Record<string, unknown> | undefined;
        const serialized = pickSerializable(edge as unknown as Record<string, unknown>, EDGE_KEYS);
        if (!existing || JSON.stringify(existing) !== JSON.stringify(serialized)) {
          this.edgesMap.set(edge.id, serialized);
        }
      }
    });
    this.isLocalUpdate = false;
  }

  // ---- Subscribe to changes ----

  onNodesChange(cb: (nodes: ReactFlowNode<NodeData>[]) => void): () => void {
    this.nodesChangeCallbacks.push(cb);
    return () => {
      this.nodesChangeCallbacks = this.nodesChangeCallbacks.filter((c) => c !== cb);
    };
  }

  onEdgesChange(cb: (edges: ReactFlowEdge<LinkData>[]) => void): () => void {
    this.edgesChangeCallbacks.push(cb);
    return () => {
      this.edgesChangeCallbacks = this.edgesChangeCallbacks.filter((c) => c !== cb);
    };
  }

  // ---- Awareness ----

  setLocalCursor(cursor: CursorPosition, user: AwarenessUser): void {
    if (this.disposed) return;
    this.socket.emit('awareness-update', {
      canvasId: this.canvasId,
      awareness: { cursor, user },
    });
  }

  onRemoteCursors(cb: (cursors: RemoteCursor[]) => void): () => void {
    this.remoteCursorsCallbacks.push(cb);
    return () => {
      this.remoteCursorsCallbacks = this.remoteCursorsCallbacks.filter((c) => c !== cb);
    };
  }

  // ---- Emit helpers ----

  private emitNodesChange(): void {
    const nodes = this.collectNodes();
    for (const cb of this.nodesChangeCallbacks) {
      cb(nodes);
    }
  }

  private emitEdgesChange(): void {
    const edges = this.collectEdges();
    for (const cb of this.edgesChangeCallbacks) {
      cb(edges);
    }
  }

  private emitRemoteCursors(): void {
    const cursors = Array.from(this.remoteCursors.values());
    for (const cb of this.remoteCursorsCallbacks) {
      cb(cursors);
    }
  }

  private collectNodes(): ReactFlowNode<NodeData>[] {
    const nodes: ReactFlowNode<NodeData>[] = [];
    this.nodesMap.forEach((value: unknown) => {
      nodes.push(value as ReactFlowNode<NodeData>);
    });
    return nodes;
  }

  private collectEdges(): ReactFlowEdge<LinkData>[] {
    const edges: ReactFlowEdge<LinkData>[] = [];
    this.edgesMap.forEach((value: unknown) => {
      edges.push(value as ReactFlowEdge<LinkData>);
    });
    return edges;
  }

  // ---- Cleanup ----

  destroy(): void {
    this.disposed = true;
    this.socket.emit('leave-canvas', { canvasId: this.canvasId });
    this.socket.off('sync-state', this.handleSyncState);
    this.socket.off('yjs-update', this.handleRemoteUpdate);
    this.socket.off('awareness-update', this.handleAwarenessUpdate);
    this.doc.off('update', this.handleDocUpdate);
    this.doc.destroy();
    this.nodesChangeCallbacks = [];
    this.edgesChangeCallbacks = [];
    this.remoteCursorsCallbacks = [];
    this.remoteCursors.clear();
  }
}
